import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import Eyebrow from '@/components/Common/Eyebrow';
import Link from 'next/link';
import {
    contentMaxWidth,
    borderRadius,
    colors,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeightRatio,
    shadows,
    zIndex
} from '@/styles/tokens';
import parse from 'html-react-parser';
import {sectionHeadingSx} from '@/styles/sectionHeading';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProcessStep {
    stepNumber: number;
    category: string;
    heading: string;
    description: string;
    /** Omit for a copy-only step — the deliverables column is then skipped. */
    deliverables?: string[];
    duration?: string;
    deliverable?: string;
    /** When set, the banner label renders inline before the text (e.g. "Client value"). */
    deliverableLabel?: string;
}

export interface HomeDeliveryProcessProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    steps: ProcessStep[];
    whiteCards?: boolean;
}

// ─── Sx constants ─────────────────────────────────────────────────────────────

const stepNumberBaseSx = {
    width: {xs: 40, md: 48},
    height: {xs: 40, md: 48},
    borderRadius: borderRadius.circle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative' as const,
    zIndex: zIndex.raised,
    boxShadow: shadows.ring
} as const;

const categorySx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.accent.orange,
    mb: 0.75
} as const;

const deliverableLabelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.secondary,
    mb: 1.5
} as const;

const deliverableBannerSx = {
    mt: {xs: 2.5, md: 3},
    p: {xs: 2, md: 2.5},
    bgcolor: colors.background.tag,
    borderLeft: `3px solid ${colors.primary.main}`,
    borderRadius: borderRadius.md
} as const;

const deliverableBannerLabelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.primary,
    mb: 0.75
} as const;

const inlineBannerLabelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.primary
} as const;

const tagSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 2,
    py: 1,
    borderRadius: borderRadius.pill,
    border: `1px solid ${colors.neutral.borderLight}`,
    backgroundColor: colors.background.tag,
    fontSize: fontSize[12],
    fontWeight: fontWeight.medium,
    color: colors.primary.main,
    lineHeight: lineHeightRatio.normal
} as const;

const durationRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mt: 2
} as const;

const stepRowSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
    gap: {xs: 2, md: 4},
    alignItems: 'flex-start'
} as const;

const singleColumnRowSx = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: {xs: 2, md: 4},
    alignItems: 'flex-start'
} as const;

// ─── StepContent ─────────────────────────────────────────────────────────────

const StepContent = memo(({step}: {step: ProcessStep}) => (
    <Box>
        <Box sx={step.deliverables?.length ? stepRowSx : singleColumnRowSx}>
            {/* Left: step info */}
            <Box>
                <Typography component="p" className="font-headers" sx={categorySx}>
                    {step.category}
                </Typography>
                <Typography
                    component="h3"
                    className="font-headers"
                    sx={{
                        fontSize: fontSize[18],
                        fontWeight: fontWeight.bold,
                        mb: 1.5,
                        lineHeight: lineHeightRatio.snug
                    }}
                >
                    {step.heading}
                </Typography>
                <Typography
                    component="p"
                    className="font-regular-text"
                    sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: lineHeightRatio.loose}}
                >
                    {step.description}
                </Typography>
            </Box>

            {/* Right: deliverables */}
            {step.deliverables && step.deliverables.length > 0 && (
                <Box sx={{pt: {xs: 1, md: 0.5}}}>
                    <Typography component="p" className="font-headers" sx={deliverableLabelSx}>
                        Deliverables
                    </Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                        {step.deliverables.map((item, i) => (
                            <Box key={i} component="span" sx={tagSx}>
                                <Typography
                                    component="span"
                                    className="font-headers"
                                    sx={{fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit'}}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    {step.duration && (
                        <Box sx={durationRowSx}>
                            <Typography component="span" sx={{fontSize: {xs: fontSize[12], md: fontSize[14]}, color: colors.text.muted}}>
                                ⏱
                            </Typography>
                            <Typography
                                component="span"
                                className="font-headers"
                                sx={{
                                    fontSize: fontSize[14],
                                    fontWeight: fontWeight.semibold,
                                    color: colors.accent.orange
                                }}
                            >
                                {step.duration}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
        {step.deliverable && (
            <Box sx={deliverableBannerSx}>
                {step.deliverableLabel ? (
                    <Typography
                        component="p"
                        className="font-regular-text"
                        sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: lineHeightRatio.loose}}
                    >
                        <Typography component="span" className="font-headers" sx={inlineBannerLabelSx}>
                            {step.deliverableLabel}:{' '}
                        </Typography>
                        {step.deliverable}
                    </Typography>
                ) : (
                    <>
                        <Typography component="p" className="font-headers" sx={deliverableBannerLabelSx}>
                            Deliverable
                        </Typography>
                        <Typography
                            component="p"
                            className="font-regular-text"
                            sx={{fontSize: fontSize[16], color: colors.text.primary, lineHeight: lineHeightRatio.loose}}
                        >
                            {step.deliverable}
                        </Typography>
                    </>
                )}
            </Box>
        )}
    </Box>
));
StepContent.displayName = 'StepContent';

// ─── Main component ───────────────────────────────────────────────────────────

const HomeDeliveryProcess = ({eyebrow, heading, subheading, steps, whiteCards}: HomeDeliveryProcessProps) => {
    const CIRCLE_SIZE_MD = 48;
    const CIRCLE_SIZE_XS = 40;

    return (
        <Box component="section" className="top-space mobile-top-padding bottom-spacing mobile-padding-bottom">
            <MUIContainer>
                <Box textAlign="center" maxWidth={contentMaxWidth.standard} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
                    <Typography variant="h1" component="h2" mb={1.5} className="font-headers" sx={sectionHeadingSx}>
                        {heading}
                    </Typography>
                    {subheading && (
                        <Typography variant="subtitle1" component="p" className="font-regular-text" sx={{color: colors.text.primary}}>
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>

                {/* Steps — continuous vertical line behind all circles */}
                <Box sx={{position: 'relative'}}>
                    {/* Single continuous line from top of first circle to top of last circle */}
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            left: {xs: `${CIRCLE_SIZE_XS / 2}px`, md: `${CIRCLE_SIZE_MD / 2}px`},
                            top: {xs: `${CIRCLE_SIZE_XS / 2}px`, md: `${CIRCLE_SIZE_MD / 2}px`},
                            bottom: {xs: `${CIRCLE_SIZE_XS / 2}px`, md: `${CIRCLE_SIZE_MD / 2}px`},
                            width: '2px',
                            background: `linear-gradient(to bottom, ${colors.primary.main}, ${colors.accent.orange})`,
                            transform: 'translateX(-50%)',
                            zIndex: zIndex.base
                        }}
                    />

                    {steps.map((step, index) => {
                        const isLast = index === steps.length - 1;
                        const dotColor = isLast ? colors.accent.orange : colors.primary.main;

                        return (
                            <Box
                                key={step.stepNumber}
                                sx={{
                                    display: 'flex',
                                    gap: {xs: 2, md: 3},
                                    alignItems: 'flex-start',
                                    mb: {xs: 3, md: 4}
                                }}
                            >
                                {/* Circle */}
                                <Box
                                    sx={{
                                        ...stepNumberBaseSx,
                                        backgroundColor: dotColor
                                    }}
                                >
                                    <Typography
                                        className="font-headers"
                                        sx={{
                                            color: colors.text.inverse,
                                            fontSize: {xs: fontSize[16], md: fontSize[18]},
                                            fontWeight: fontWeight.bold,
                                            lineHeight: 1
                                        }}
                                    >
                                        {step.stepNumber}
                                    </Typography>
                                </Box>

                                {/* Step content */}
                                <Box
                                    sx={
                                        whiteCards
                                            ? {
                                                  flex: 1,
                                                  bgcolor: colors.background.default,
                                                  border: `1px solid ${colors.neutral.borderLight}`,
                                                  borderRadius: borderRadius['2xl'],
                                                  boxShadow: shadows.lg,
                                                  p: {xs: 2.5, md: 4}
                                              }
                                            : {flex: 1, pt: 0.5}
                                    }
                                >
                                    <StepContent step={step} />
                                    {!whiteCards && !isLast && (
                                        <Divider sx={{mt: {xs: 4, md: 5}, borderColor: colors.neutral.borderLight}} />
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{textAlign: 'center', mt: {xs: 6, md: 8}}}>
                    <Link href="/contact-us" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                        <MUIButton
                            label="Discuss Your Project Requirements"
                            variant="contained"
                            size="medium"
                            endIcon={<ArrowForwardIcon />}
                            className="animated-button button-bg-primary moving-icon-button font-headers"
                        />
                    </Link>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(HomeDeliveryProcess);
